import { useRef, type ChangeEvent, type ReactNode } from 'react';
import { CloseOutlined, SearchOutlined } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, type SxProps, type Theme } from '@mui/material';
import { createFocusRingStyles } from '../interaction';
import { createInputSurfaceStyles } from '../inputSurface';
import { designTokens } from '../tokens';
import { FormTextField } from './FormTextField';

export type SearchFieldProps = {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  placeholder?: string;
  /** 목록 도구에서는 이름을 보조기기에 제공하고 폼 안에서는 라벨을 표시합니다. */
  hideLabel?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  sx?: SxProps<Theme>;
};

/** 검색 요청·debounce·결과 상태는 호출 화면이 소유합니다. */
export function SearchField({ value, onValueChange, label, placeholder = '검색', hideLabel = true, disabled = false, readOnly = false, error = false, helperText, sx }: SearchFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const canClear = Boolean(value) && !disabled && !readOnly;
  const styles: SxProps<Theme> = {
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      ...createInputSurfaceStyles({ variant: 'notched', disabled, invalid: error, focusIndicator: 'inset' }),
      height: designTokens.layout.controlMinHeight,
      borderRadius: designTokens.radius.search,
      bgcolor: designTokens.color.canvas,
      p: 0,
      ...designTokens.typography.ui,
      fontWeight: 400,
      [designTokens.media.touchTarget]: { height: designTokens.layout.touchTargetMinSize },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderRadius: 'inherit' },
    '& .MuiOutlinedInput-root .MuiInputBase-input': {
      minWidth: 0,
      ...designTokens.typography.ui,
      py: `${designTokens.space.xs}px`,
      pl: 0,
      pr: canClear ? 0 : `${designTokens.space.xs}px`,
      [designTokens.media.touchTarget]: { fontSize: designTokens.typography.body.fontSize },
    },
    '& .MuiInputBase-input::placeholder': { color: designTokens.color.mutedText, opacity: 1 },
    '& .MuiInputAdornment-root': {
      m: 0,
      width: designTokens.layout.controlMinHeight,
      height: designTokens.layout.controlMinHeight,
      maxHeight: 'none',
      flexShrink: 0,
      justifyContent: 'center',
      [designTokens.media.touchTarget]: { width: designTokens.layout.touchTargetMinSize, height: designTokens.layout.touchTargetMinSize },
    },
  };
  const inputProps = {
    inputRef,
    fullWidth: true,
    value,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onValueChange(event.target.value),
    placeholder,
    autoComplete: 'off',
    disabled,
    error,
    helperText,
    slotProps: {
      input: {
        readOnly,
        startAdornment: <InputAdornment position="start" disablePointerEvents><SearchOutlined sx={{ fontSize: designTokens.layout.searchIconSize }} /></InputAdornment>,
        endAdornment: canClear ? <InputAdornment position="end">
          <IconButton
            aria-label="검색어 지우기"
            size="small"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { onValueChange(''); inputRef.current?.focus(); }}
            sx={{
              '& > .MuiSvgIcon-root': { fontSize: designTokens.layout.searchIconSize },
              '&.Mui-focusVisible, &:focus-visible': { ...createFocusRingStyles({ inset: true }), boxShadow: 'none' },
            }}
          ><CloseOutlined /></IconButton>
        </InputAdornment> : undefined,
      },
      htmlInput: { 'aria-label': label, enterKeyHint: 'search' as const },
    },
    sx: [styles, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])],
  };
  return hideLabel ? <TextField {...inputProps} /> : <FormTextField {...inputProps} label={label} />;
}
